import { forwardRef, useCallback, useRef } from 'react';
import {
    type ColorlessIconProps,
    HStack,
    IconHelp,
    UserBar,
    UserBarItem,
    UserBarTopSection,
    VStack,
} from '@direct-frontend/components';
import { Outlet } from '@direct-frontend/modules-router';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { UserBarThemeSwitcher } from '#features/ChangeTheme';
import { EmailAuthUserBarItem } from '#features/EmailAuth';
import { SnowflakesButton } from '#shared/components/SnowflakesButton';
import { SupportChatButton, SupportChatProvider } from '#shared/components/SupportChat';
import { YandexIdUserBarItem } from '#shared/components/YandexIdUserBarItem';
import { KIT_LITE_SUPPORT_URL, KIT_SUPPORT_URL_WITH_UTM, MERCHANTS_SUPPORT_URL } from '#shared/lib/const';
import { getSafeUserRequestContext } from '#shared/lib/context';
import { openInNewTab } from '#shared/lib/router';
import { checkIsLiteStore } from '#shared/lib/storeInfo';
import { checkIsYCP } from '#shared/lib/ycp';
import { AIAssistant } from '#widgets/AIAssistant';
import { AppShellProvider } from '#widgets/AppShellProvider';
import { DashboardAlertsButton } from '#widgets/DashboardAlerts';
import { DashboardMarketingLinksButton } from '#widgets/DashboardMarketingLinksButton';
import { DashboardNewsLink } from '#widgets/DashboardNews';
import { PreviewSidePanel } from '#widgets/PreviewSidePanel';
import { TopBar, TopBarLayoutWrapper } from '#widgets/TopBar';
import { getMenuSections } from '../DashboardLayout';
import { DashboardSidebar } from '../DashboardLayout/DashboardSidebar';
import { YcpServiceAlert } from './YcpServiceAlert';
import styles from './ApplicationLayout@desktop.module.css';

function getSupportUrl({ isLite, isYCP }: { isLite: boolean; isYCP: boolean }): string {
    if (isLite) {
        return KIT_LITE_SUPPORT_URL;
    }

    if (isYCP) {
        return MERCHANTS_SUPPORT_URL;
    }

    return KIT_SUPPORT_URL_WITH_UTM;
}

function ApplicationLayout() {
    const isYCP = checkIsYCP();
    const isLite = checkIsLiteStore();
    const aiAssistantWindowSlotRef = useRef<HTMLDivElement | null>(null);
    const { activeAuthMethod } = getSafeUserRequestContext();
    const setPortalContainerRef = useRef<((el: HTMLElement | null) => void) | null>(null);

    const registerPortalContainer = useCallback((setter: ((el: HTMLElement | null) => void) | null) => {
        setPortalContainerRef.current = setter;

        if (aiAssistantWindowSlotRef.current != null && setter != null) {
            setter(aiAssistantWindowSlotRef.current);
        }
    }, []);

    const aiAssistantSlotCallbackRef = useCallback((el: HTMLDivElement | null) => {
        aiAssistantWindowSlotRef.current = el;
        setPortalContainerRef.current?.(el);
    }, []);

    return (
        <SupportChatProvider headless={!isYCP}>
            <TopBarLayoutWrapper>
                <VStack className={styles.root}>
                    <YcpServiceAlert />
                    <TopBar />
                    <AppShellProvider>
                        <HStack justifyContent="space-between" className={styles.horizontal}>
                            <DashboardSidebar menuSections={getMenuSections()} sticky />
                            <Outlet />
                            <div ref={aiAssistantSlotCallbackRef} className={styles.aiAssistantWindowSlot} />
                            <VStack gap="4" className={styles.buttonsContainer}>
                                <UserBar className={styles.userBar}>
                                    <UserBarTopSection
                                        user={
                                            activeAuthMethod === 'email' ? (
                                                <EmailAuthUserBarItem />
                                            ) : (
                                                <YandexIdUserBarItem
                                                    platform="desktop"
                                                    dialogClassName={styles.userDialog}
                                                />
                                            )
                                        }
                                        children={
                                            <>
                                                <DashboardAlertsButton className={styles.userBarChild} />
                                                {!isYCP && <DashboardNewsLink className={styles.userBarChild} />}
                                                <SnowflakesButton className={styles.userBarChild} />
                                                <UserBarItem
                                                    className={styles.userBarChild}
                                                    icon={forwardRef<SVGSVGElement, ColorlessIconProps>(
                                                        function HelpIcon(props, ref) {
                                                            return (
                                                                <IconHelp
                                                                    {...props}
                                                                    ref={ref}
                                                                    title="Перейти в справку"
                                                                    color="gray"
                                                                    role="supplementary"
                                                                />
                                                            );
                                                        }
                                                    )}
                                                    onClick={() => {
                                                        openInNewTab(getSupportUrl({ isYCP, isLite }));
                                                        logEventString('ApplicationLayout.HelpButtonClick', 'true');
                                                    }}
                                                />
                                                <DashboardMarketingLinksButton className={styles.userBarChild} />
                                            </>
                                        }
                                    />
                                </UserBar>
                                <VStack gap="4" className={styles.sidebarBottom} alignItems="center">
                                    <UserBarThemeSwitcher />
                                    {isYCP ? (
                                        <SupportChatButton />
                                    ) : (
                                        <AIAssistant
                                            className={styles.aiAssistantSlot}
                                            registerPortalContainer={registerPortalContainer}
                                        />
                                    )}
                                </VStack>
                            </VStack>
                            <PreviewSidePanel />
                        </HStack>
                    </AppShellProvider>
                </VStack>
            </TopBarLayoutWrapper>
        </SupportChatProvider>
    );
}

export default ApplicationLayout;
